import React, { useEffect, useState } from "react";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Plus, Trash, PencilSimple, Receipt } from "@phosphor-icons/react";
import { toast } from "sonner";

const empty = { name: "", rate: 2, description: "", sales_account_code: "", purchase_account_code: "" };

export default function TaxRegulations() {
  const [regs, setRegs] = useState([]);
  const [accounts, setAccounts] = useState([]);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(empty);

  const load = () => {
    api.get("/tax-regulations").then(r => setRegs(r.data)).catch(() => {});
    api.get("/accounting/accounts").then(r => setAccounts((r.data || []).filter(a => !a.is_parent))).catch(() => {});
  };
  useEffect(() => { load(); }, []);

  const save = async () => {
    try {
      const payload = { ...form, rate: Number(form.rate) };
      if (editing) await api.put(`/tax-regulations/${editing.id}`, payload);
      else await api.post("/tax-regulations", payload);
      toast.success("Regulasi pajak tersimpan");
      setOpen(false); setEditing(null); setForm(empty); load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Gagal simpan"); }
  };
  const edit = (r) => { setEditing(r); setForm({ ...r }); setOpen(true); };
  const del = async (r) => {
    if (!window.confirm(`Hapus regulasi ${r.name}?`)) return;
    try { await api.delete(`/tax-regulations/${r.id}`); toast.success("Dihapus"); load(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Gagal"); }
  };
  const accLabel = (code) => { const a = accounts.find(x => x.code === code); return a ? `${a.code} - ${a.name}` : code || "—"; };
  const openNew = () => { setEditing(null); setForm(empty); setOpen(true); };

  return (
    <div className="p-4 sm:p-6 lg:p-8 max-w-6xl" data-testid="tax-regulations-page">
      <div className="flex items-start justify-between flex-wrap gap-3">
        <div>
          <h1 className="font-serif-display text-4xl font-medium text-slate-900">Regulasi Pajak</h1>
          <p className="text-sm text-slate-500 mt-1">Master pajak potongan (PPh 21/23/4(2)/dll). Setelah dibuat, tempelkan ke pelanggan/vendor lewat menu Team → Pelanggan &amp; Vendor. Saat faktur dibuat untuk partner tsb, potongan otomatis dihitung dari DPP dan di-jurnal ke akun yang dipetakan.</p>
        </div>
        <Button className="bg-amber-600 hover:bg-amber-700" onClick={openNew} data-testid="new-tax-reg-btn"><Plus size={16} className="mr-1" />Regulasi Baru</Button>
      </div>

      <div className="mt-6 bg-white border border-slate-200 rounded-md" data-testid="tax-regulations-table">
        <Table>
          <TableHeader><TableRow>
            <TableHead>Nama</TableHead><TableHead>%</TableHead><TableHead>Akun Penjualan</TableHead><TableHead>Akun Pembelian</TableHead><TableHead>Keterangan</TableHead><TableHead className="w-24"></TableHead>
          </TableRow></TableHeader>
          <TableBody>
            {regs.length === 0 ? <TableRow><TableCell colSpan={6} className="text-center py-12 text-slate-500">
              <Receipt size={32} className="mx-auto mb-2 text-slate-300" />
              <p className="text-sm">Belum ada regulasi pajak. Buat pertama Anda (misal: PPh 23 Jasa 2%).</p>
            </TableCell></TableRow> : regs.map(r => (
              <TableRow key={r.id} data-testid={`tax-reg-row-${r.id}`}>
                <TableCell className="font-medium">{r.name}</TableCell>
                <TableCell className="tabular-nums font-mono-plex">{r.rate}%</TableCell>
                <TableCell className="text-xs text-slate-600">{accLabel(r.sales_account_code)}</TableCell>
                <TableCell className="text-xs text-slate-600">{accLabel(r.purchase_account_code)}</TableCell>
                <TableCell className="text-xs text-slate-500 max-w-xs truncate" title={r.description}>{r.description || "—"}</TableCell>
                <TableCell><div className="flex gap-1">
                  <button onClick={() => edit(r)} className="text-slate-400 hover:text-amber-600" data-testid={`edit-tax-reg-${r.id}`}><PencilSimple size={14} /></button>
                  <button onClick={() => del(r)} className="text-slate-400 hover:text-rose-600" data-testid={`del-tax-reg-${r.id}`}><Trash size={14} /></button>
                </div></TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>

      <Dialog open={open} onOpenChange={(o) => { setOpen(o); if (!o) { setEditing(null); setForm(empty); } }}>
        <DialogContent>
          <DialogHeader><DialogTitle>{editing ? "Edit Regulasi Pajak" : "Regulasi Pajak Baru"}</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div><Label>Nama</Label><Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder="cth: PPh 23 Jasa" data-testid="tax-reg-name" /></div>
            <div><Label>Nilai % <span className="text-[11px] text-slate-500 font-normal">(0-100)</span></Label><Input type="number" step="0.01" value={form.rate} onChange={(e) => setForm({ ...form, rate: e.target.value })} placeholder="2" data-testid="tax-reg-rate" /></div>
            <div><Label>Keterangan</Label><Textarea rows={2} value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} data-testid="tax-reg-desc" /></div>
            <div><Label>Akun Penjualan <span className="text-[11px] text-slate-500 font-normal">(dijurnal saat faktur penjualan mengandung potongan ini)</span></Label>
              <Select value={form.sales_account_code} onValueChange={(v) => setForm({ ...form, sales_account_code: v })}>
                <SelectTrigger data-testid="tax-reg-sales-acc"><SelectValue placeholder="Pilih akun (mis. 1550 PPh 23 Dibayar Dimuka)" /></SelectTrigger>
                <SelectContent className="max-h-72">{accounts.map(a => <SelectItem key={a.code} value={a.code}>{a.code} - {a.name}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div><Label>Akun Pembelian <span className="text-[11px] text-slate-500 font-normal">(dijurnal saat faktur pembelian mengandung potongan ini)</span></Label>
              <Select value={form.purchase_account_code} onValueChange={(v) => setForm({ ...form, purchase_account_code: v })}>
                <SelectTrigger data-testid="tax-reg-purchase-acc"><SelectValue placeholder="Pilih akun (mis. 2120 Hutang PPh 23)" /></SelectTrigger>
                <SelectContent className="max-h-72">{accounts.map(a => <SelectItem key={a.code} value={a.code}>{a.code} - {a.name}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <Button onClick={save} disabled={!form.name || !form.sales_account_code || !form.purchase_account_code} className="w-full bg-slate-900" data-testid="tax-reg-save">Simpan</Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
